import {
  isSystemAccessCode,
  isAgencyAccessCode,
  type AgencyAccessCode,
  type AssignedUserRole,
  type SystemAccessCode
} from '@gat-ship/types'
import { useUserApiCalls } from '../composables/useUserApiCalls'
import { type ComputedRef, ref } from 'vue'

export function useUserAccessControlDataSource(
  isTenantAdmin: ComputedRef<boolean>
) {
  const { getAccessRolesRequest } = useUserApiCalls()
  const accessroles = ref<AssignedUserRole[]>([])

  function _setAccessCodes(codes: AssignedUserRole[]) {
    accessroles.value = codes
  }

  const rolePermissionMap = ref<Record<string, string[]>>({})

  function _setRolePermissionMap(map: Record<string, string[]>) {
    rolePermissionMap.value = map
  }

  async function fetchAccessCodes() {
    const result = await getAccessRolesRequest()
    _setAccessCodes(result.roles)
    _setRolePermissionMap(result.rolePermissionMap)
  }

  async function refreshAccessCodes() {
    /*  console.warn(
      'Refreshing access codes with a temporary solution. This must be reworked in the future.'
    ) */
    const result = await getAccessRolesRequest()
    _setAccessCodes(result.roles)
    _setRolePermissionMap(result.rolePermissionMap)
  }

  function hasPermission(accessCode: SystemAccessCode) {
    if (isTenantAdmin.value) {
      return true
    }
    if (!isSystemAccessCode(accessCode)) {
      console.warn(
        'Access code is not an defined system access code',
        accessCode
      )
    }
    const accessCodes = Object.values(rolePermissionMap.value).flat()
    return accessCodes.includes(accessCode)
  }

  function hasAgencyPermissions(
    accessCode: AgencyAccessCode,
    agencyNodeId: string
  ) {
    if (isTenantAdmin.value) {
      return true
    }
    if (!isAgencyAccessCode(accessCode)) {
      console.warn(
        'Access code is not an defined agency access code',
        accessCode
      )
    }
    const rolesForAgencyNode = [...accessroles.value].filter(
      (role) => role.agencyNodeId === agencyNodeId
    )
    // console.log('accessroles', accessroles.value)
    const permissionsForRoles = rolesForAgencyNode
      .map((role) => rolePermissionMap.value[role.id])
      .filter(
        (permissions): permissions is string[] => permissions !== undefined
      )
    return permissionsForRoles.some((permissions) =>
      permissions.includes(accessCode)
    )
  }

  function hasRole(roleId: string) {
    return accessroles.value.some((role) => role.id === roleId)
  }

  return {
    accessroles,
    rolePermissionMap,
    fetchAccessCodes,
    refreshAccessCodes,
    hasPermission,
    hasAgencyPermissions,
    hasRole
  }
}
